import React, { useState, useEffect, useCallback } from 'react';
import {
    Plus,
    Search,
    MoreVertical,
    CheckCircle,
    AlertCircle,
    ChevronDown,
    ChevronLeft,
    ChevronRight,
    Mail,
    Trash2,
    RefreshCw,
    UserMinus,
    Shield,
    Clock,
    X
} from 'lucide-react';
import { workspaceApi } from '../../services/api';
import { useWorkspace } from '../../context/WorkspaceContext';
import { useAuth } from '../../context/AuthContext';
import Dialog from '../../components/Dialog';
import './WorkspaceMembers.css';

const ROLES = ['owner', 'admin', 'member', 'viewer'];
const INVITABLE_ROLES = ['admin', 'member', 'viewer'];

function WorkspaceMembers() {
    const { currentWorkspace } = useWorkspace();
    const { user: currentUser } = useAuth();
    const [activeTab, setActiveTab] = useState('active');
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedRole, setSelectedRole] = useState('All Roles');

    const [members, setMembers] = useState([]);
    const [invitations, setInvitations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [errorType, setErrorType] = useState(null);

    // Invite modal state
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [inviteEmail, setInviteEmail] = useState('');
    const [inviteRole, setInviteRole] = useState('member');
    const [inviteMessage, setInviteMessage] = useState('');
    const [inviting, setInviting] = useState(false);

    // Action menu state
    const [activeActionMenu, setActiveActionMenu] = useState(null);

    // Dialog state
    const [dialog, setDialog] = useState({
        isOpen: false,
        title: '',
        message: '',
        type: 'confirm',
        variant: 'default',
        onConfirm: null
    });

    // Get current user's role in workspace
    const currentUserMember = members.find(m => m.userId === currentUser?.id);
    const currentUserRole = currentUserMember?.role || 'viewer';
    const isAdmin = ['owner', 'admin'].includes(currentUserRole);
    const isOwner = currentUserRole === 'owner';

    const fetchMembers = useCallback(async () => {
        if (!currentWorkspace?.id) return;

        try {
            const response = await workspaceApi.getMembers(currentWorkspace.id);
            setMembers(response.data.members || []);
        } catch (err) {
            console.error('Failed to fetch members:', err);
            setError('Failed to load members');
        }
    }, [currentWorkspace?.id]);

    const fetchInvitations = useCallback(async () => {
        if (!currentWorkspace?.id || !isAdmin) return;

        try {
            const response = await workspaceApi.getInvitations(currentWorkspace.id);
            setInvitations(response.data.invitations || []);
        } catch (err) {
            console.error('Failed to fetch invitations:', err);
        }
    }, [currentWorkspace?.id, isAdmin]);

    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            await Promise.all([fetchMembers(), fetchInvitations()]);
            setLoading(false);
        };
        loadData();
    }, [fetchMembers, fetchInvitations]);

    const showAlert = (title, message, variant = 'default') => {
        setDialog({
            isOpen: true,
            title,
            message,
            type: 'alert',
            variant,
            onConfirm: null
        });
    };

    const handleInvite = async (e) => {
        e.preventDefault();
        if (!inviteEmail.trim()) return;

        setInviting(true);
        try {
            await workspaceApi.inviteMember(
                currentWorkspace.id,
                inviteEmail.trim(),
                inviteRole,
                inviteMessage.trim() || undefined
            );
            setShowInviteModal(false);
            setInviteEmail('');
            setInviteRole('member');
            setInviteMessage('');
            fetchInvitations();

            // Show success message
            showAlert('Invitation Sent', `An invitation has been sent to ${inviteEmail}`, 'success');
        } catch (err) {
            showAlert('Failed to Invite', err.response?.data?.error || 'Failed to send invitation', 'danger');
        } finally {
            setInviting(false);
        }
    };

    const handleRevokeInvite = (inviteId) => {
        setDialog({
            isOpen: true,
            title: 'Revoke Invitation',
            message: 'Are you sure you want to revoke this invitation? The link will no longer work.',
            type: 'confirm',
            variant: 'danger',
            confirmText: 'Revoke',
            onConfirm: () => executeRevokeInvite(inviteId)
        });
    };

    const executeRevokeInvite = async (inviteId) => {
        try {
            await workspaceApi.revokeInvitation(currentWorkspace.id, inviteId);
            fetchInvitations();
        } catch (err) {
            showAlert('Error', err.response?.data?.error || 'Failed to revoke invitation', 'danger');
        }
    };

    const handleResendInvite = async (inviteId) => {
        try {
            await workspaceApi.resendInvitation(currentWorkspace.id, inviteId);
            showAlert('Invitation Resent', 'The invitation email has been sent again.', 'success');
            fetchInvitations();
        } catch (err) {
            showAlert('Error', err.response?.data?.error || 'Failed to resend invitation', 'danger');
        }
    };

    const handleUpdateRole = async (userId, newRole) => {
        try {
            await workspaceApi.updateMemberRole(currentWorkspace.id, userId, newRole);
            fetchMembers();
        } catch (err) {
            showAlert('Error', err.response?.data?.error || 'Failed to update role', 'danger');
        }
        setActiveActionMenu(null);
    };

    const handleRemoveMember = (userId, memberName) => {
        setDialog({
            isOpen: true,
            title: 'Remove Member',
            message: `Are you sure you want to remove ${memberName} from this workspace? They will lose access to all projects and data.`,
            type: 'confirm',
            variant: 'danger',
            confirmText: 'Remove',
            onConfirm: () => executeRemoveMember(userId)
        });
    };

    const executeRemoveMember = async (userId) => {
        try {
            await workspaceApi.removeMember(currentWorkspace.id, userId);
            fetchMembers();
        } catch (err) {
            showAlert('Error', err.response?.data?.error || 'Failed to remove member', 'danger');
        }
        setActiveActionMenu(null);
    };

    // Filter logic
    const filteredMembers = members.filter(member => {
        const matchesSearch =
            (member.fullName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            (member.email || '').toLowerCase().includes(searchTerm.toLowerCase());
        const matchesRole = selectedRole === 'All Roles' || member.role === selectedRole.toLowerCase();
        return matchesSearch && matchesRole;
    });

    const filteredInvitations = invitations.filter(inv => {
        return inv.email.toLowerCase().includes(searchTerm.toLowerCase());
    });

    const getInitials = (name) => {
        if (!name) return '?';
        return name
            .split(' ')
            .map(part => part[0])
            .join('')
            .toUpperCase()
            .substring(0, 2);
    };

    const getRoleBadgeClass = (role) => {
        switch (role?.toLowerCase()) {
            case 'owner': return 'owner';
            case 'admin': return 'admin';
            case 'member': return 'editor';
            case 'viewer': return 'viewer';
            default: return 'viewer';
        }
    };

    const formatTimeAgo = (date) => {
        if (!date) return 'Never';
        const now = new Date();
        const then = new Date(date);
        const diffMs = now - then;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins} min ago`;
        if (diffHours < 24) return `${diffHours} hours ago`;
        if (diffDays < 7) return `${diffDays} days ago`;
        return then.toLocaleDateString();
    };

    if (loading) {
        return (
            <div className="workspace-members-page">
                <div className="loading-state">Loading members...</div>
            </div>
        );
    }

    return (
        <div className="workspace-members-page">
            <div className="page-header">
                <div className="header-content">
                    <h1>Workspace Members</h1>
                    {/* <p>Manage access to your workspace. Assign roles to control who can run tests, view reports, or manage settings.</p> */}
                </div>
                {isAdmin && (
                    <button className="invite-btn" onClick={() => setShowInviteModal(true)}>
                        <Plus size={18} />
                        Invite
                    </button>
                )}
            </div>

            <div className="tabs-container">
                <button
                    className={`tab-btn ${activeTab === 'active' ? 'active' : ''}`}
                    onClick={() => setActiveTab('active')}
                >
                    Active Members <span className="tab-count">{members.length}</span>
                </button>
                {isAdmin && (
                    <button
                        className={`tab-btn ${activeTab === 'pending' ? 'active' : ''}`}
                        onClick={() => setActiveTab('pending')}
                    >
                        Pending Invites <span className="tab-count">{invitations.length}</span>
                    </button>
                )}
            </div>

            <div className="filters-bar">
                <div className="search-wrapper">
                    <Search className="search-icon" size={16} />
                    <input
                        type="text"
                        placeholder="Search members by name or email..."
                        className="search-input"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                {activeTab === 'active' && (
                    <div className="role-filter">
                        <select
                            className="role-dropdown-btn"
                            value={selectedRole}
                            onChange={(e) => setSelectedRole(e.target.value)}
                        >
                            <option>All Roles</option>
                            {ROLES.map(role => (
                                <option key={role} value={role}>{role.charAt(0).toUpperCase() + role.slice(1)}</option>
                            ))}
                        </select>
                    </div>
                )}
            </div>

            {error && <div className="error-banner">{error}</div>}

            <div className="members-table-container">
                {activeTab === 'active' ? (
                    <table className="members-table">
                        <thead>
                            <tr>
                                <th>USER</th>
                                <th>ROLE</th>
                                <th>JOINED</th>
                                <th>LAST ACTIVE</th>
                                {isAdmin && <th style={{ textAlign: 'right' }}>ACTIONS</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {filteredMembers.map(member => (
                                <tr key={member.id}>
                                    <td>
                                        <div className="user-cell">
                                            <div className="user-avatar">
                                                <span>{getInitials(member.fullName || member.email)}</span>
                                            </div>
                                            <div className="user-info">
                                                <span className="user-name">
                                                    {member.fullName || member.email?.split('@')[0]}
                                                    {member.userId === currentUser?.id && <span className="you-badge">You</span>}
                                                </span>
                                                <span className="user-email">{member.email}</span>
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        {isAdmin && member.role !== 'owner' && member.userId !== currentUser?.id ? (
                                            <select
                                                className={`role-badge ${getRoleBadgeClass(member.role)}`}
                                                value={member.role}
                                                onChange={(e) => handleUpdateRole(member.userId, e.target.value)}
                                            >
                                                {(isOwner ? ['admin', 'member', 'viewer'] : ['member', 'viewer']).map(role => (
                                                    <option key={role} value={role}>
                                                        {role.charAt(0).toUpperCase() + role.slice(1)}
                                                    </option>
                                                ))}
                                            </select>
                                        ) : (
                                            <div className={`role-badge ${getRoleBadgeClass(member.role)}`}>
                                                {member.role?.charAt(0).toUpperCase() + member.role?.slice(1)}
                                            </div>
                                        )}
                                    </td>
                                    <td>{formatTimeAgo(member.joinedAt)}</td>
                                    <td>{formatTimeAgo(member.lastActive)}</td>
                                    {isAdmin && (
                                        <td style={{ textAlign: 'right' }}>
                                            {member.role !== 'owner' && member.userId !== currentUser?.id && (
                                                <button
                                                    className="action-btn danger"
                                                    onClick={() => handleRemoveMember(member.userId, member.fullName || member.email)}
                                                    title="Remove member"
                                                >
                                                    <UserMinus size={16} />
                                                </button>
                                            )}
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                ) : filteredInvitations.length === 0 ? (
                    <div className="empty-state-container">
                        <p className="empty-state-text">No pending invitations</p>
                    </div>
                ) : (
                    <table className="members-table">
                        <thead>
                            <tr>
                                <th>EMAIL</th>
                                <th>ROLE</th>
                                <th>INVITED BY</th>
                                <th>EXPIRES</th>
                                <th style={{ textAlign: 'right' }}>ACTIONS</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredInvitations.map(inv => (
                                <tr key={inv.id} className={inv.isExpired ? 'expired' : ''}>
                                    <td>
                                        <div className="user-cell">
                                            <div className="user-avatar pending">
                                                <Mail size={16} />
                                            </div>
                                            <div className="user-info">
                                                <span className="user-email">{inv.email}</span>
                                                {inv.isExpired && <span className="expired-badge">Expired</span>}
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <div className={`role-badge ${getRoleBadgeClass(inv.role)}`}>
                                            {inv.role?.charAt(0).toUpperCase() + inv.role?.slice(1)}
                                        </div>
                                    </td>
                                    <td>{inv.invitedBy}</td>
                                    <td>
                                        <span className={inv.isExpired ? 'text-danger' : ''}>
                                            {new Date(inv.expiresAt).toLocaleDateString()}
                                        </span>
                                    </td>
                                    <td style={{ textAlign: 'right' }}>
                                        <button
                                            className="action-btn"
                                            onClick={() => handleResendInvite(inv.id)}
                                            title="Resend invitation"
                                        >
                                            <RefreshCw size={16} />
                                        </button>
                                        <button
                                            className="action-btn danger"
                                            onClick={() => handleRevokeInvite(inv.id)}
                                            title="Revoke invitation"
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}

                <div className="pagination-footer">
                    <span>
                        Showing {activeTab === 'active' ? filteredMembers.length : filteredInvitations.length} of {activeTab === 'active' ? members.length : invitations.length} results
                    </span>
                </div>
            </div>

            {/* Invite Modal */}
            {showInviteModal && (
                <div className="modal-overlay" onClick={() => setShowInviteModal(false)}>
                    <div className="modal-content" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Invite Member</h2>
                            <button className="modal-close" onClick={() => setShowInviteModal(false)}>
                                <X size={20} />
                            </button>
                        </div>
                        <form onSubmit={handleInvite}>
                            <div className="form-group">
                                <label>Email Address</label>
                                <input
                                    type="email"
                                    value={inviteEmail}
                                    onChange={e => setInviteEmail(e.target.value)}
                                    placeholder="colleague@company.com"
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label>Role</label>
                                <select value={inviteRole} onChange={e => setInviteRole(e.target.value)}>
                                    {INVITABLE_ROLES.map(role => (
                                        <option key={role} value={role}>
                                            {role.charAt(0).toUpperCase() + role.slice(1)}
                                        </option>
                                    ))}
                                </select>
                                <span className="help-text">
                                    {inviteRole === 'admin' && 'Can manage workspace settings and members'}
                                    {inviteRole === 'member' && 'Can create and run tests'}
                                    {inviteRole === 'viewer' && 'Can view tests and reports'}
                                </span>
                            </div>
                            <div className="form-group">
                                <label>Personal Message (optional)</label>
                                <textarea
                                    value={inviteMessage}
                                    onChange={e => setInviteMessage(e.target.value)}
                                    placeholder="Add a personal note to the invitation..."
                                    rows={3}
                                />
                            </div>
                            <div className="modal-actions">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowInviteModal(false)}>
                                    Cancel
                                </button>
                                <button type="submit" className="btn btn-primary" disabled={inviting}>
                                    {inviting ? 'Sending...' : 'Send Invitation'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Custom Dialog */}
            <Dialog
                isOpen={dialog.isOpen}
                onClose={() => setDialog(prev => ({ ...prev, isOpen: false }))}
                title={dialog.title}
                message={dialog.message}
                type={dialog.type}
                variant={dialog.variant}
                confirmText={dialog.confirmText}
                onConfirm={dialog.onConfirm}
            />
        </div>
    );
}

export default WorkspaceMembers;
